/**
 * Column-type renderer registry for EntityGrid.
 *
 * Maps the column `type` descriptor from the backend grid-spec onto MUI X
 * DataGrid Pro column definitions. Handles string, integer/number, datetime,
 * boolean, enum (chip), reference ({type,id,name} chip with route link),
 * and actions (icon-button cluster, or a labeled contained button per action
 * key when the caller opts in via `actionHandlers`). The descriptor's
 * snake_case wire keys are read once by `normalizeDescriptorColumn`; this
 * module consumes the normalized shape and merges FE per-field display
 * overrides on top of the type defaults.
 *
 * ODL-specific dependencies (reference resolution, formatters, icon registry,
 * i18n) are threaded in via the `opts` parameter of buildColumnsFromSpec rather
 * than imported directly, so the renderers are usable in generic contexts.
 * Callers that omit opts slots fall back to the same hardcoded behavior that
 * existed before the config seam was introduced.
 *
 * `ReferenceChip` is also exported directly for a caller whose grid has a
 * field the descriptor doesn't (yet) declare as `type: "reference"` — a
 * `fieldOverrides` renderCell can render the same chip the descriptor's own
 * reference columns use instead of hand-building a link.
 */

import { Button, Chip, Link, Stack, Tooltip } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import StatusChip from '@sophocles/components/StatusChip';
import { resolveColumnSizing } from '@sophocles/data-grids/grid/columnSizing';
import { refLabel, refRoute } from '@sophocles/features/entities/references';

import { resolveDescriptorLabel } from './resolveDescriptorLabel';
import { normalizeDescriptorColumn } from './normalizeDescriptorColumn';
import { formatGridDateTime } from './valueFormats';

// ---------------------------------------------------------------------------
// Internal cell renderers
// ---------------------------------------------------------------------------

/**
 * Renders a boolean value as a status pill (true → ok, false → neutral).
 *
 * @param {{ value: boolean|null|undefined, booleanFormatter: Function|undefined }} props
 */
function BooleanChip({ value, booleanFormatter }) {
  const { t } = useTranslation();
  if (value === null || value === undefined) {
    return <StatusChip label="—" color="neutral" dot={false} />;
  }
  // Formatter is injected from config.formatters.boolean; falls back to the
  // original hardcoded labels for callers that don't pass a formatter.
  const label = booleanFormatter ? booleanFormatter(value) : value ? t('general.yes') : t('general.no');
  return <StatusChip label={label} color={value ? 'ok' : 'neutral'} />;
}

/**
 * Renders an enum value as a status pill.
 *
 * The spec column carries an optional `enumOptions` map of
 * `{ [value]: { label, color } }` where `color` is a status-palette family.
 * Falls back to a neutral pill when the value isn't found in the map.
 */
function EnumChip({ value, enumOptions }) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const opt = enumOptions?.[value];
  return <StatusChip label={opt?.label ?? String(value)} color={opt?.color ?? 'neutral'} />;
}

/**
 * Renders a cross-entity reference as a chip that links to the entity's route.
 * The ref object has the shape { type, id, name }.
 *
 * `resolveRef` is injected from config.resolveReference; when absent the
 * component falls back to the original refLabel/refRoute helpers (preserving
 * behavior for callers that don't pass the config opts).
 *
 * `linked=false` renders the chip without its route link — for grids whose
 * rows are themselves the click target (e.g. orders opening their drawer),
 * where an entity link inside the row hijacks the click.
 *
 * @param {{ value: object|null, resolveReference: Function|undefined, linked: boolean }} props
 */
export function ReferenceChip({ value, resolveReference: resolveRef, linked = true }) {
  if (!value) return null;
  // Use the injected resolver when available; otherwise fall back to the
  // hardcoded ODL reference helpers so existing direct callers are unchanged.
  const { label, route } = resolveRef ? resolveRef(value) : { label: refLabel(value), route: refRoute(value) };

  const chip = <Chip size="small" label={label || String(value.id)} variant="outlined" />;

  if (route && linked) {
    return (
      <Link href={route} underline="none" onClick={e => e.stopPropagation()}>
        {chip}
      </Link>
    );
  }
  return chip;
}

/**
 * Renders an `actions` cell from a spec column that carries an `actions`
 * array of `{ key, label_key, icon }` descriptors. Each entry renders as a
 * Tooltip-wrapped icon button by default.
 *
 * `actionHandlers[key]` is either a bare `(row) => void` handler (the icon
 * button above) or a `{ onClick, variant: 'button' }` object — the latter
 * renders a labeled contained button instead of an icon, for a single action
 * a caller wants to read as the row's primary affordance rather than a
 * secondary icon. The presentation choice is opt-in per action key and
 * caller-owned, so other actions/adopters are unaffected.
 *
 * `resolveActionIcon` is injected from config.resolveActionIcon and resolves
 * a string icon name to an MUI component. When absent, `icon` is treated as
 * a component directly (pre-config legacy path). `translate` resolves the
 * descriptor's `label_key` for the tooltip/button label; without it the
 * literal `label` or the action `key` is shown.
 */
function ActionsCell({ row, actions, actionHandlers, resolveActionIcon: resolveIcon, translate }) {
  if (!actions?.length) return null;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {actions.map(({ key, label, label_key: labelKey, icon }) => {
        const entry = actionHandlers?.[key];
        if (!entry) return null;
        const isButton = typeof entry === 'object' && entry.variant === 'button';
        const handler = isButton ? entry.onClick : entry;
        // Label: translate the descriptor's label_key when a translator is
        // available, falling back to a literal label or the action key.
        const title = translate ? translate(labelKey ?? label ?? key, label ?? key) : (label ?? key);

        if (isButton) {
          return (
            <Button
              key={key}
              variant="contained"
              color="primary"
              size="small"
              onClick={e => {
                e.stopPropagation();
                handler(row);
              }}
            >
              {title}
            </Button>
          );
        }

        // When resolveIcon is provided (config context), treat icon as a string
        // name resolved through the registry. Without it, treat icon as a
        // component directly for hand-coded action columns (pre-config path).
        const Icon = resolveIcon ? resolveIcon(icon) : icon;
        return (
          <Tooltip key={key} title={title}>
            <span>
              {Icon ? (
                <Icon
                  fontSize="small"
                  sx={{ cursor: 'pointer', color: 'action.active' }}
                  onClick={e => {
                    e.stopPropagation();
                    handler(row);
                  }}
                />
              ) : null}
            </span>
          </Tooltip>
        );
      })}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Type → column-def factory map
// ---------------------------------------------------------------------------

/**
 * Base defaults shared by all types; each type can narrow or override. Sizing
 * is deliberately absent — `resolveColumnSizing` states every column's width in
 * full, so a leftover default here would survive into types it does not suit.
 *
 * @type {import('@mui/x-data-grid-pro').GridColDef}
 */
const BASE_DEFAULTS = {
  sortable: true,
};

// Maps the backend descriptor's semantic `tone` token onto a status-palette
// family (cssTokens.js --status-*). The FE owns this mapping so the contract
// stays decoupled from the FE palette. Covers both tone vocabularies the
// descriptor emits — enum options (success/warning/danger) and stat cards
// (positive/negative/attention) — so a token has one family wherever it lands.
export const TONE_TO_FAMILY = {
  neutral: 'neutral',
  info: 'info',
  success: 'ok',
  positive: 'ok',
  warning: 'warning',
  attention: 'attention',
  danger: 'bad',
  negative: 'bad',
};

// Legacy descriptor `color` keys (MUI palette names, pre-`tone` dual-emit
// window) onto the same families.
const LEGACY_COLOR_TO_FAMILY = {
  default: 'neutral',
  info: 'info',
  success: 'ok',
  warning: 'warning',
  error: 'bad',
  secondary: 'attention',
};

/**
 * Maps the backend descriptor's enum `options` (`[{ value, label_key, tone }]`)
 * onto the `{ [value]: { label, color } }` shape `EnumChip` expects, where
 * `color` is a status-palette family. `label_key` is run through `t` when a
 * translator is supplied. The semantic `tone` token drives the family
 * (falling back to the legacy MUI `color` key during the dual-emit window).
 *
 * @param {object[]} [options]  Descriptor enum options.
 * @param {Function} [t]        i18next translator.
 * @returns {object|undefined}
 */
function buildEnumOptions(options, t) {
  if (!Array.isArray(options)) return undefined;
  const map = {};
  for (const opt of options) {
    map[opt.value] = {
      label: t ? t(opt.label_key, opt.label_key) : opt.label_key,
      color: opt.tone ? (TONE_TO_FAMILY[opt.tone] ?? 'neutral') : (LEGACY_COLOR_TO_FAMILY[opt.color] ?? 'neutral'),
    };
  }
  return map;
}

/**
 * Builds a partial MUI X column definition from a normalized descriptor column.
 * The caller merges FE overrides on top of what this returns.
 *
 * ODL-specific dependencies are passed via `opts` so this function is usable
 * in generic contexts. When an opt is absent the column falls back to the same
 * hardcoded behavior that existed before the config seam was introduced.
 *
 * @param {object} col  A normalized column from `normalizeDescriptorColumn`.
 * @param {object} [overrides]  FE per-field display overrides (merged last).
 * @param {object} [actionHandlers]  Map of `{ [key]: (row) => void | { onClick, variant: 'button' } }` for action columns.
 * @param {object} [opts]
 * @param {Function} [opts.t]                i18next translator for header labels and enum options.
 * @param {object}   [opts.formatters]        config.formatters — boolean and datetime formatters.
 * @param {Function} [opts.resolveReference]  config.resolveReference — resolves { type, id, name } to { label, route }.
 * @param {Function} [opts.resolveActionIcon] config.resolveActionIcon — resolves icon name string to MUI component.
 * @param {object} [opts.terms]                 Tenant vocabulary, passed as the i18n interpolation context for every header.
 * @returns {import('@mui/x-data-grid-pro').GridColDef}
 */
function buildColumnDef(
  col,
  overrides = {},
  actionHandlers = {},
  { t, formatters, resolveReference, resolveActionIcon, terms, referenceLinks = true } = {},
) {
  const {
    field,
    type,
    headerLabel,
    headerFallback,
    sortable,
    filterable,
    width,
    minWidth,
    flex,
    align,
    options,
    actions,
    format,
    editable,
    hideable,
    defaultPinned,
  } = col;

  // Header from the normalized label_key, translated with the descriptor's
  // literal `label` (or the field name) as the default value — so a spec with
  // a real i18n key resolves the translation and one whose key has no entry
  // still shows the human label. With no translator the header is the
  // fallback text, preserving the pre-i18n behaviour.
  //
  // Shared with the stat cards, so a header and a card title cannot drift on
  // which terms their copy may name — see resolveDescriptorLabel.
  const headerName = resolveDescriptorLabel(t, headerLabel, headerFallback, terms);

  // Resolved once per column rather than per render: the chip renderer reads
  // the same map, and an enum column is sized to the widest label in it.
  const enumOptions = type === 'enum' ? buildEnumOptions(options, t) : undefined;

  // What the column's values need. An actions cluster is the one column whose
  // width is a count of buttons rather than a data shape, so it sets its own in
  // the switch below.
  const sizing = resolveColumnSizing(
    { type, width, minWidth, flex },
    { headerName, optionLabels: enumOptions ? Object.values(enumOptions).map(opt => opt.label) : undefined },
  );
  if (align) {
    sizing.align = align;
    sizing.headerAlign = align;
  }

  /** @type {import('@mui/x-data-grid-pro').GridColDef} */
  const base = {
    ...BASE_DEFAULTS,
    field,
    headerName,
    sortable,
    filterable,
    editable,
    // Descriptor-driven hideability. Placed before the overrides spread so a
    // fieldOverride can still win (e.g. a grid that pins a column visible).
    hideable,
    // A pinned cell sits over the horizontally scrolling ones, so it needs an
    // opaque paint of its own or the columns sliding underneath show through.
    // `cell-pinned` is the anchor-strip tint ServerDataGrid keys that paint on.
    ...(defaultPinned ? { cellClassName: 'cell-pinned' } : {}),
    ...sizing,
  };

  switch (type) {
    case 'integer':
    case 'number':
      return {
        ...base,
        type: 'number',
        ...overrides,
      };

    case 'datetime':
      return {
        ...base,
        // format="date" marks a date-only value with no timezone; the shared
        // formatter reads those as local calendar dates. Mobile cards state
        // the same values, so both surfaces read from one implementation.
        valueFormatter: value =>
          formatGridDateTime(value, { dateOnly: format === 'date', datetimeFormatter: formatters?.datetime }),
        ...overrides,
      };

    case 'boolean':
      return {
        ...base,
        renderCell: ({ value }) => <BooleanChip value={value} booleanFormatter={formatters?.boolean} />,
        ...overrides,
      };

    case 'enum':
      return {
        ...base,
        renderCell: ({ value }) => <EnumChip value={value} enumOptions={enumOptions} />,
        ...overrides,
      };

    case 'reference':
      return {
        ...base,
        sortable: false,
        renderCell: ({ value }) => (
          <ReferenceChip value={value} resolveReference={resolveReference} linked={referenceLinks} />
        ),
        ...overrides,
      };

    case 'actions':
      return {
        ...base,
        field: field ?? '__actions__',
        // headerName flows through from `base` (resolved from the descriptor's
        // label_key, e.g. "Actions") rather than being blanked here — the
        // columns show/hide panel reads headerName, so blanking it left the
        // panel showing the raw field name ("actions") instead of a real label.
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: width ?? (actions?.length ?? 1) * 40,
        flex: undefined,
        renderCell: ({ row }) => (
          <ActionsCell
            row={row}
            actions={actions}
            actionHandlers={actionHandlers}
            resolveActionIcon={resolveActionIcon}
            translate={t}
          />
        ),
        ...overrides,
      };

    case 'string':
    default:
      return {
        ...base,
        ...overrides,
      };
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Converts the grid-spec `columns` array into MUI X DataGrid Pro column
 * definitions.
 *
 * Takes the descriptor's `columns` array — NOT the full descriptor object.
 * The `/grid-spec/` endpoint resolves (via getGridSpecFor) to
 * `{ columns, default_sort, … }`; every caller extracts `.columns` before
 * calling this. One schema, one access pattern.
 *
 * @param {object[]} specColumns  The descriptor's `columns` array.
 * @param {object}   [fieldOverrides]  Map of `{ [field]: Partial<GridColDef> }` for
 *   FE-side per-field display overrides. These are merged last and win over the
 *   type defaults.
 * @param {object}   [actionHandlers]  Map of `{ [key]: (row) => void | { onClick, variant: 'button' } }`
 *   for action columns. Ignored for non-action columns.
 * @param {object}   [opts]
 * @param {Function} [opts.t]                i18next translator used to resolve column `label_key`
 *   headers. Pass it for fully descriptor-driven headers; omit to keep raw field
 *   names (and supply `headerName` via `fieldOverrides`).
 * @param {object}   [opts.formatters]        config.formatters — overrides boolean/datetime cell rendering.
 *   When absent, the original hardcoded formatters are used so existing callers are unchanged.
 * @param {Function} [opts.resolveReference]  config.resolveReference — resolves a { type, id, name }
 *   ref to { label, route }. When absent, falls back to refLabel/refRoute from references.js.
 * @param {Function} [opts.resolveActionIcon] config.resolveActionIcon — resolves an icon name string
 *   to an MUI component. When absent, the icon value is used as a component directly.
 * @returns {import('@mui/x-data-grid-pro').GridColDef[]}
 */
export function buildColumnsFromSpec(
  specColumns,
  fieldOverrides = {},
  actionHandlers = {},
  { t, formatters, resolveReference, resolveActionIcon, terms, referenceLinks = true } = {},
) {
  if (!Array.isArray(specColumns)) return [];
  return (
    specColumns
      .map(col => normalizeDescriptorColumn(col))
      // payload_only entries exist solely for the export path — no screen column.
      .filter(normalized => !normalized.payloadOnly)
      .map(normalized =>
        buildColumnDef(normalized, fieldOverrides[normalized.field], actionHandlers, {
          t,
          formatters,
          resolveReference,
          resolveActionIcon,
          terms,
          referenceLinks,
        }),
      )
  );
}

/**
 * Derives the initial `columnVisibilityModel` from the grid-spec `columns`
 * array: every column flagged `default_hidden` starts hidden. Columns the user
 * can re-show via the grid-settings menu.
 *
 * @param {object[]} specColumns  The descriptor's `columns` array.
 * @returns {Object<string, boolean>}  `{ [field]: false }` for default-hidden columns.
 */
export function buildColumnVisibilityModel(specColumns) {
  if (!Array.isArray(specColumns)) return {};
  const model = {};
  for (const col of specColumns) {
    const { field, defaultHidden, payloadOnly } = normalizeDescriptorColumn(col);
    if (payloadOnly) continue;
    if (defaultHidden) model[field] = false;
  }
  return model;
}
